import isEqual from 'lodash/isEqual';

import {createCheckboxes, createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const defaultIntervalSeconds = '1';
const defaultMaxAttempts = '3';
const defaultBackoffRate = '2.0';

const retryFields = ['retryIntervalSeconds', 'retryMaxAttempts', 'retryBackoffRate'];

const normalizeRetryTask = task => {
    if (!isPlainObject(task)) {
        return {};
    }

    return cloneValue(task);
};

const hasRetryFields = retryTask => retryFields.some(fieldName => retryTask[fieldName]);

const getResource = (mxResources, key) => mxResources.get(key);

const fieldNameFor = (name, taskValuePath) => taskValuePath ? `${taskValuePath}.${name}` : name;

const fieldIdFor = (name, taskValuePath) => taskValuePath ? `${taskValuePath}${name.charAt(0).toUpperCase()}${name.slice(1)}` : name;

const createParagraph = text => {
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    return paragraph;
};

const createExampleItem = ({label, value}) => {
    const item = document.createElement('li');
    const labelElement = document.createElement('strong');
    labelElement.textContent = label;
    item.appendChild(labelElement);
    item.appendChild(document.createTextNode(value));
    return item;
};

const appendRetryExample = (container, mxResources) => {
    container.appendChild(createParagraph(getResource(mxResources, 'retryExampleIntro')));

    const list = document.createElement('ul');
    list.appendChild(createExampleItem({
        label: getResource(mxResources, 'retryIntervalSeconds'),
        value: getResource(mxResources, 'retryExampleIntervalSecondsValue')
    }));
    list.appendChild(createExampleItem({
        label: getResource(mxResources, 'retryMaxAttempts'),
        value: getResource(mxResources, 'retryExampleMaxAttemptsValue')
    }));
    list.appendChild(createExampleItem({
        label: getResource(mxResources, 'retryBackoffRate'),
        value: getResource(mxResources, 'retryExampleBackoffRateValue')
    }));
    container.appendChild(list);
};

const normalizeRetryPatch = patch => retryFields.reduce((normalizedPatch, fieldName) => {
    if (hasOwnProperty(patch, fieldName)) {
        normalizedPatch[fieldName] = typeof patch[fieldName] === 'undefined' ? null : patch[fieldName];
    }
    return normalizedPatch;
}, {});

const createRetryForm = ({
                             task = {},
                             retryErrors = {},
                             editable = true,
                             mxResources,
                             taskValuePath = null,
                             showHelp = true,
                             onRetryChange = () => {}
                         } = {}) => {
    let retryTask = normalizeRetryTask(taskValuePath ? task[taskValuePath] || {} : task);

    const container = document.createElement('div');
    container.id = taskValuePath ? `${taskValuePath}RetryEditor` : 'retryEditor';
    container.className = 'ui form';
    if (showHelp) {
        container.appendChild(createParagraph(getResource(mxResources, 'taskErrorHandlingHelp')));
    }

    const fieldsSlot = document.createElement('div');
    fieldsSlot.className = 'retry-fields';
    container.appendChild(fieldsSlot);

    const emitRetry = patch => {
        const normalizedPatch = normalizeRetryPatch(patch);
        const nextRetryTask = normalizeRetryTask({
            ...retryTask,
            ...normalizedPatch
        });

        if (isEqual(retryTask, nextRetryTask)) {
            return;
        }

        retryTask = nextRetryTask;
        if (taskValuePath) {
            task[taskValuePath] = cloneValue(retryTask);
            onRetryChange({[taskValuePath]: cloneValue(retryTask)}, cloneValue(retryTask));
            render();
            return;
        }

        Object.assign(task, normalizedPatch);
        onRetryChange(cloneValue(normalizedPatch), cloneValue(retryTask));
        render();
    };

    const render = () => {
        fieldsSlot.replaceChildren();
        const hasRetry = hasRetryFields(retryTask);

        const typeFields = createCheckboxes({
            label: getResource(mxResources, 'taskErrorHandlingType'),
            name: fieldNameFor('taskErrorHandlingType', taskValuePath),
            value: hasRetry ? 'yes' : 'no',
            type: 'radio',
            className: 'required',
            options: [{
                label: getResource(mxResources, 'taskErrorHandlingNo'),
                className: 'inline',
                value: 'no',
                onChange: () => emitRetry({
                    retryIntervalSeconds: null,
                    retryMaxAttempts: null,
                    retryBackoffRate: null
                })
            }, {
                label: getResource(mxResources, 'taskErrorHandlingYes'),
                className: 'inline',
                value: 'yes',
                onChange: () => emitRetry({
                    retryIntervalSeconds: retryTask.retryIntervalSeconds || defaultIntervalSeconds,
                    retryMaxAttempts: retryTask.retryMaxAttempts || defaultMaxAttempts,
                    retryBackoffRate: retryTask.retryBackoffRate || defaultBackoffRate
                })
            }]
        });
        fieldsSlot.appendChild(typeFields);

        if (!hasRetry) {
            return;
        }

        const intervalSecondsField = createInputField({
            label: getResource(mxResources, 'retryIntervalSeconds'),
            id: fieldIdFor('retryIntervalSeconds', taskValuePath),
            name: fieldNameFor('retryIntervalSeconds', taskValuePath),
            type: 'number',
            min: 1,
            value: retryTask.retryIntervalSeconds,
            placeHolder: defaultIntervalSeconds,
            help: getResource(mxResources, 'retryIntervalSecondsHelp'),
            error: retryErrors?.intervalSeconds
        });
        intervalSecondsField.querySelector('input').addEventListener('change', event => emitRetry({retryIntervalSeconds: event.target.value}));
        fieldsSlot.appendChild(intervalSecondsField);

        const maxAttemptsField = createInputField({
            label: getResource(mxResources, 'retryMaxAttempts'),
            id: fieldIdFor('retryMaxAttempts', taskValuePath),
            name: fieldNameFor('retryMaxAttempts', taskValuePath),
            type: 'number',
            min: 0,
            value: retryTask.retryMaxAttempts,
            placeHolder: defaultMaxAttempts,
            help: getResource(mxResources, 'retryMaxAttemptsHelp'),
            error: retryErrors?.maxAttempts
        });
        maxAttemptsField.querySelector('input').addEventListener('change', event => emitRetry({retryMaxAttempts: event.target.value}));
        fieldsSlot.appendChild(maxAttemptsField);

        const backoffRateField = createInputField({
            label: getResource(mxResources, 'retryBackoffRate'),
            id: fieldIdFor('retryBackoffRate', taskValuePath),
            name: fieldNameFor('retryBackoffRate', taskValuePath),
            type: 'number',
            min: '1.0',
            step: '0.1',
            placeHolder: defaultBackoffRate,
            value: retryTask.retryBackoffRate,
            help: getResource(mxResources, 'retryBackoffRateHelp'),
            error: retryErrors?.backoffRate
        });
        backoffRateField.querySelector('input').addEventListener('change', event => emitRetry({retryBackoffRate: event.target.value}));
        fieldsSlot.appendChild(backoffRateField);

        appendRetryExample(fieldsSlot, mxResources);
    };

    render();

    if (!editable) {
        disableFormControls(container);
    }

    return {
        element: container,
        getRetryTask: () => cloneValue(retryTask),
        setRetryTask: value => {
            retryTask = normalizeRetryTask(value);
            if (taskValuePath) {
                task[taskValuePath] = cloneValue(retryTask);
            } else {
                retryFields.forEach(fieldName => {
                    task[fieldName] = retryTask[fieldName];
                });
            }
            render();
        }
    };
};

export {
    createRetryForm,
    defaultBackoffRate,
    defaultIntervalSeconds,
    defaultMaxAttempts,
    hasRetryFields,
    normalizeRetryPatch,
    normalizeRetryTask
};
